<script setup lang="ts">
import { useStore } from "vuex";
import { isEmpty } from "lodash";
import { computed } from "vue";
import { materialProduct } from "@/types/product";

const i18n = window.measurementSelectorVariables.i18n;
const store = useStore();
const currentProductMaterial = computed(() => store.getters.currentProductMaterial);
const alternateMaterials = computed(() => store.getters.alternateMaterials);
const baseShapeInstance = computed(() => store.getters.baseShapeInstance);

function pricePerSquareMeter( option: materialProduct ): string {
	return `${option.price} / ${option.unit} ${option.suffix}`;
}

async function setMaterial( material: materialProduct ): Promise<void> {
	if ( material.id === currentProductMaterial.value.id ) {
		return;
	}

	store.commit('updateMaterial', material);
	const url = await getUrl( material );

	if ( url === '' ) {
		return;
	}

	location.href = url;
}

async function getUrl( material: materialProduct ): Promise<string> {
	if ( isEmpty( baseShapeInstance.value ) ) {
		return material.permalink;
	}

	return await store.dispatch( 'generateShareableLink', {
		url: material.permalink,
		isTemporary: true
	} );
}

</script>
<template>
	<v-select
		label="title"
		class="material-selector"
		inputId="measurements-selector-material-select"
		v-model="currentProductMaterial"
		:options="alternateMaterials"
		:clearable="false"
		:placeholder="i18n.material.selectMaterial"
		@update:modelValue="setMaterial"
	>
		<template v-slot:option="option">
			<div class="material-selector-teaser">
				<div class="material-selector-teaser__thumbnail-container">
					<img class="material-selector-teaser__image" :src="option.image" alt="" width="60" height="60" loading="lazy" v-if="option.image" />
				</div>
				<div class="material-selector-teaser__inner">
					<span class="material-selector-teaser__title">{{ option.title }}</span>
					<span class="material-selector-teaser__subtitle" v-if="option.price" v-html="pricePerSquareMeter( option )"></span>
				</div>
			</div>
		</template>

		<template #selected-option="option">
			<div class="material-selector-teaser">
				<div class="material-selector-teaser__thumbnail-container">
					<img class="material-selector-teaser__image" :src="option.image" alt="" width="60" height="60" loading="lazy" v-if="option.image" />
				</div>
				<div class="material-selector-teaser__inner">
					<span class="material-selector-teaser__title">{{ option.title }}</span>
					<span class="material-selector-teaser__subtitle" v-if="option.price" v-html="pricePerSquareMeter( option )"></span>
				</div>
			</div>
		</template>

	</v-select>
</template>
<style scoped lang="scss">

.material-selector {
	width: 100%;
}
.material-selector-teaser {
	display: flex;
	align-items: center;

	&__thumbnail-container {
		position: relative;
		margin-right: $default-padding * 0.5;
		width: 60px;
		height: 60px;
		background: $brand-blue-background;
	}

	&__image {
		position: absolute;
		top: 50%;
		transform: translateY(-50%);
		mix-blend-mode: multiply;
	}

	&__inner {
		display: flex;
		flex-direction: column;
		max-width: 70%;
	}

	&__title {
		@include font-size(16px, 18px);
		color: $brand-blue-dark;
		font-weight: bold;
		white-space: normal;
	}

	&__subtitle {
		@include font-size(14px, 16px);
		color: $brand-blue-dark;
	}
}

</style>
